import { FC, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import cs from 'classnames';
import { useMethods } from '@lhb/hook';
import { isArray } from '@lhb/func';
import { getYouthMapModelClusterGetSettledInfo } from '@/common/api/youth';
import { LabelType, OpenStatus, typeLabelImgMap } from 'src/common/components/business/YouthMap/AreaDetailDrawer/ts-config';
import { XTCLabel } from '@/views/youth/pages/map/ts-config';
import { getLabelRelations } from '@/common/api/networkplan';
import { getLabelShareRelations } from '@/common/api/siteselectionmap';
import styles from './index.module.less';
import TagGroup from 'src/common/components/TagGroup/index';

/**
 * 标签统筹展示
 * @description 标签顺序：系统标签（项目等级、商圈类型、入驻情况及其他） > 双向推荐 > 已规划 > 自定义标签
 * @demo
 */
const Component:FC<{
  /** 商圈详情 */
  detail: {
    id: number,
    /** 系统标签（项目等级、商圈类型、入驻情况及其他） */
    labels?: any[],
    /** 项目等级（S级） */
    projectLevel?: string,
    /** 竞品门店标签（小米门店） */
    competitorList?: any[],
  },
  /** 概览数据 */
  overviewData?: any,
  labelRef?: any,
  /** 分享页传入数据 */
  shareParams?,
  /** 更新标签数据到父级 */
  updateAllLabels?: (labels: any[]) => void,
  className?: any,
  style?: any,
  ref?: any,
  setCustomLabels?: any;
}> = forwardRef(({
  detail,
  overviewData,
  labelRef,
  updateAllLabels,
  shareParams,
  className,
  style,
  setCustomLabels,
}, ref) => {

  useImperativeHandle(ref, () => ({
    reload: methods.reload,
  }));

  /* state */
  /** 关联标签 */
  const [relationsLabels, setRelationsLabels] = useState<any>({});
  /** 入驻情况（已入驻） */
  const [merchantSettledFlag, setMerchantSettledFlag] = useState<number>(0);

  /** 所有标签：系统标签（项目等级、商圈类型、入驻情况及其他） > 双向推荐 > 已规划 > 自定义标签 */
  const allLabels: {
    /** 标签名称 */
    label?: string,
    /** 标签图片，优先级：img > label */
    img?: string,
    imgWidth?: any,
    imgHeight?: any,
  }[] = useMemo(() => {

    // 1 系统标签
    const curSystemLabels = relationsLabels[LabelType.System];

    // 2 网规标签
    const netPlanLabels = relationsLabels[LabelType.NetPlan];
    const netPlanLabelImg = typeLabelImgMap.get(netPlanLabels?.[0]?.name || '') || '';

    // 3 自定义标签
    const curCustomLabels = relationsLabels[LabelType.Custom];

    /** 详情系统标签，取自 detail?.labels + overviewData?.labels 去重合并：美食型、中档、时尚潮流、小康人群、中年品质型 */
    const detailLabels = Array.from(new Set([
      ...(Array.isArray(detail?.labels) ? detail?.labels : []),
      ...(Array.isArray(overviewData?.labels) ? overviewData?.labels : []),
    ])).map(item => ({ label: item }));

    /** 竞品门店标签（小米门店），取自 detail?.competitorList + overviewData?.competitorList 去重合并：大地影院、MCDONALD'S麦当劳、哥老官、喜姐炸串、... */
    const competitorLabels = Array.from(new Set([
      ...(Array.isArray(detail?.competitorList) ? detail?.competitorList : []),
      ...(Array.isArray(overviewData?.competitorList) ? overviewData?.competitorList : []),
    ])).map(item => ({ label: item }));

    // 项目等级（S级）
    const projectLevel = detail?.projectLevel || overviewData?.projectLevel;

    const {
      /** 已规划 */
      childCompanyPlanned,
      /** 双向推荐 */
      doubleRecommendation,
      /** 是否行业竞争 */
      industryCompetition
    } = overviewData || {};

    const isWaitOpen = overviewData?.openStatus === OpenStatus.WaitOpen;

    return [
      // 系统标签顺序：
      // 项目等级（S级）：优先展示 img，其次 label
      projectLevel ? { label: projectLevel, img: XTCLabel[projectLevel] } : null,
      // 商圈类型（商业体）
      ...(Array.isArray(curSystemLabels) ? curSystemLabels : []).map(item => ({ label: item.name })) as any[],
      // 待开业
      isWaitOpen ? { label: '待开业' } : null,
      // 入驻情况（已入驻）
      merchantSettledFlag ? { img: `${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/youth/icon-enter-2.png` } : null,
      // 商圈系统标签（连锁名称、商业面积、....）
      ...detailLabels,
      // 竞品门店标签（小米门店）
      ...competitorLabels,
      // 双向推荐
      doubleRecommendation ? { label: '双向推荐' } : null,
      // 已规划
      childCompanyPlanned ? { label: '已规划' } : null,
      // 是否行业竞争（火焰图标）
      industryCompetition ? { img: `${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/icon_flame@2x.png` } : null,
      // 网规标签img：A类、B类、C类、D类
      netPlanLabelImg ? { img: netPlanLabelImg } : null,
      // 自定义标签
      ...(Array.isArray(curCustomLabels) ? curCustomLabels : []).map(item => ({ ...item, label: item.name, isCustomColor: true })),
    ].filter(Boolean) as any[];
  }, [detail, overviewData, merchantSettledFlag, relationsLabels]);

  /* hooks */
  useEffect(() => {
    // console.log('detail', detail);
    detail?.id && methods.reload();
  }, [detail?.id]);

  useEffect(() => {
    updateAllLabels?.(allLabels);
  }, [allLabels]);

  /* methods */
  const methods = useMethods({

    reload() {
      methods.getMerchantSettledFlag();
      methods.getRelationsLabels();
    },

    /** 【青春版】商圈是否已有本品牌在营门店入驻 */
    getMerchantSettledFlag() {
      if (!detail?.id) {
        return;
      }
      getYouthMapModelClusterGetSettledInfo({ id: detail.id }).then((res) => {
        setMerchantSettledFlag(res.merchantSettledFlag);
      });
    },
    /**
    * 请求商圈标签
    * @description 商圈id变动、以及标签编辑后
    */
    getRelationsLabels: () => {
      if (!detail?.id) {
        return;
      }
      setRelationsLabels({});
      let api = getLabelRelations;
      if (shareParams?.tenantId) {
        api = getLabelShareRelations;
      }
      // 当前商圈设定的网规标签、自定义标签
      api({ modelClusterId: detail.id, tenantId: shareParams?.tenantId }).then((data) => {
        setRelationsLabels(data);
        const curCustomLabels = data[LabelType.Custom];
        setCustomLabels && setCustomLabels(isArray(curCustomLabels) ? curCustomLabels : []);
      });
    },

  });

  return (<div
    ref={labelRef}
    className={cs(styles.allLabels, className)}
    style={style}
  >
    <TagGroup tags={allLabels} />
  </div>);
});

export default Component;
